/**
 * @brisa/ui · PasswordStrengthMeter
 *
 * Indicador de força de senha (0-4). MVP usa heurística simples (tamanho +
 * variedade de classes); zxcvbn pode ser adicionado em fase futura.
 *
 * Pilar P3 — feedback contínuo, não validação on-blur.
 */
import * as React from 'react';

export interface PasswordStrengthMeterProps {
  password: string;
  minScore?: number; // default 3 (do SPEC)
  labels?: Record<0 | 1 | 2 | 3 | 4, string>;
}

const DEFAULT_LABELS: Record<0 | 1 | 2 | 3 | 4, string> = {
  0: 'Muito fraca',
  1: 'Fraca',
  2: 'Razoável',
  3: 'Boa',
  4: 'Forte',
};

export function computeScore(password: string): 0 | 1 | 2 | 3 | 4 {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password) && /[^a-zA-Z0-9]/.test(password)) score++;
  return Math.min(4, score) as 0 | 1 | 2 | 3 | 4;
}

export function PasswordStrengthMeter({
  password,
  minScore = 3,
  labels = DEFAULT_LABELS,
}: PasswordStrengthMeterProps) {
  const score = computeScore(password);
  return (
    <div
      className="brisa-pwd-meter"
      role="progressbar"
      aria-valuenow={score}
      aria-valuemin={0}
      aria-valuemax={4}
      data-meets-min={score >= minScore ? 'true' : 'false'}
    >
      <div className="brisa-pwd-meter__bars">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className="brisa-pwd-meter__bar" data-filled={i < score} />
        ))}
      </div>
      <span className="brisa-pwd-meter__label">{labels[score]}</span>
    </div>
  );
}
